import { fetchCloudAuthenticated, refreshAccessTokenShared } from '../services/api';

export const AUTHED_PROBE_BACKOFF_MS = [1000, 2000, 4000];

export type AuthedProbeResult = 'ok' | 'failed' | 'cancelled';

const defaultSleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * Bounded GET /api/auth/user probe. A non-OK answer forces a token refresh through the
 * api.ts single-flight, then re-probes after backoff. Ends 'failed' — never loops forever.
 */
export async function runAuthedApiProbe(
  isCancelled: () => boolean,
  delays: number[] = AUTHED_PROBE_BACKOFF_MS,
  sleep: (ms: number) => Promise<void> = defaultSleep,
): Promise<AuthedProbeResult> {
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    try {
      const resp = await fetchCloudAuthenticated('/api/auth/user', { method: 'GET' });
      if (resp?.ok) return isCancelled() ? 'cancelled' : 'ok';
    } catch { /* treated like a non-OK answer */ }
    if (isCancelled()) return 'cancelled';
    if (attempt === delays.length) break;
    try {
      const refreshed = await refreshAccessTokenShared('bootWarmup:authedProbe');
      if (refreshed?.fatalRejected) return isCancelled() ? 'cancelled' : 'failed';
    } catch { /* retry anyway; the next probe decides */ }
    await sleep(delays[attempt]);
    if (isCancelled()) return 'cancelled';
  }
  return 'failed';
}
